import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Alert, Spinner } from '@amzn/awsui-components-react';
import type { Topic, TopicInstance } from '../../../client/IvtHubClient';
import { useTopicMutations } from '../hooks/useTopicMutations';
import { nextNotes } from '../utils/topicPriorityCycle';
import AddInstanceModal from './AddInstanceModal';
import ConfirmDialog from './ConfirmDialog';
import TopicCard from './TopicCard';
import TopicMetadataModal from './TopicMetadataModal';

interface TopicGridProps {
  active: boolean;
  editMode: boolean;
  addTopicTrigger?: number;
}

/**
 * 4-col Topic grid. All Topic data + writes flow through `useTopicMutations`
 * which talks to the real `@amzn/ivthub-client` Smithy Topic ops via
 * `topicClient`. `VITE_IVT_MOCK=1` swaps the client to MockIvtHubClient
 * (see IvtHubClient.ts `isMockMode`).
 *
 * Drag reorder is DOM-only — no `ReorderTopics` op exists yet (design §7
 * backlog), so the order resets on the next reload.
 *
 * Note on null narrowing: Smithy `@required` fields on Topic come through as
 * `string | undefined` on the SDK type. Backend guarantees `name` non-empty,
 * so we treat empty string as "skip" and key on `name ?? ''`.
 */
export default function TopicGrid({ active, editMode, addTopicTrigger }: TopicGridProps) {
  const muts = useTopicMutations(active);

  // Local DOM-only drag reorder state. Reload (any write op) resets it.
  const [domOrder, setDomOrder] = useState<string[] | null>(null);
  const dragSrcRef = useRef<string | null>(null);
  const [openTopic, setOpenTopic] = useState<Topic | null>(null);
  const [showNewTopic, setShowNewTopic] = useState(false);
  // In-flight flag OWNED BY THIS HOST for the create-Topic modal. The per-topic lock in
  // useTopicMutations is keyed on topicName, which does not exist until the user types
  // one, so that lock cannot gate the first Save — leaving the Save button live during
  // the write. Two consequences without this flag: a second click issues a SECOND write,
  // and a third hits the hook's already-locked branch, which resolves `null` (the success
  // sentinel) and closes the modal with the write still in flight, discarding whatever
  // the user typed. Every other host passes `saving={muts.isLocked(key)}` with the key
  // known up front and does not need this.
  const [creatingTopic, setCreatingTopic] = useState(false);
  // The two ConfirmDialogs keep the classified failure HERE rather than relying on the
  // page-level mutationError Alert: that Alert renders inside .ivt-topic-view, i.e.
  // behind Cloudscape's modal overlay (rgba(242,243,243,0.9)), so it is not readable
  // while the dialog is open — and the dialog deliberately stays open on failure.
  const [deleteTopicError, setDeleteTopicError] = useState<string | null>(null);
  const [deleteInstanceError, setDeleteInstanceError] = useState<string | null>(null);
  const [addInstanceFor, setAddInstanceFor] = useState<Topic | null>(null);
  const [confirmDeleteTopic, setConfirmDeleteTopic] = useState<Topic | null>(null);
  const [confirmDeleteInstance, setConfirmDeleteInstance] = useState<{
    topic: Topic;
    inst: TopicInstance;
  } | null>(null);

  const prevAddTopicTrigger = useRef(addTopicTrigger);
  useEffect(() => {
    if (addTopicTrigger && addTopicTrigger !== prevAddTopicTrigger.current) {
      setShowNewTopic(true);
    }
    prevAddTopicTrigger.current = addTopicTrigger;
  }, [addTopicTrigger]);

  // Reset DOM order whenever the upstream list changes identity.
  const upstreamSignature = (muts.topics ?? []).map((t) => t.name ?? '').join('|');
  const lastSignatureRef = useRef<string>(upstreamSignature);
  if (lastSignatureRef.current !== upstreamSignature) {
    lastSignatureRef.current = upstreamSignature;
    if (domOrder !== null) setDomOrder(null);
  }

  if (muts.loading) {
    return (
      <div className="ivt-solution-map-page__loading">
        <Spinner size="large" />
        <span>加载 Topic…</span>
      </div>
    );
  }

  if (muts.loadError) {
    return (
      <Alert type="error" header="无法加载 Topic">
        {muts.loadError}
      </Alert>
    );
  }

  const topics = muts.topics ?? [];
  const orderedTopics: Topic[] = domOrder
    ? domOrder
        .map((name) => topics.find((t) => t.name === name))
        .filter((t): t is Topic => !!t)
        .concat(topics.filter((t) => t.name && !domOrder.includes(t.name)))
    : topics;

  const handleSaveNewTopic = async (
    input: import('../../../client/topicClient').UpsertTopicInput,
  ): Promise<string | null> => {
    setCreatingTopic(true);
    try {
      const failure = await muts.upsertTopic(input);
      if (!failure) setShowNewTopic(false);
      return failure;
    } finally {
      setCreatingTopic(false);
    }
  };


  const handleSaveInstance = async (input: {
    subTopicName: string;
    industry: string;
    notes: 'S' | 'R' | 'O';
  }): Promise<string | null> => {
    // No target topic = nothing to save. Not a failure, so no message.
    if (!addInstanceFor?.name) return null;
    const failure = await muts.upsertInstance({
      topicName: addInstanceFor.name,
      industry: input.industry,
      subTopicName: input.subTopicName,
      notes: input.notes,
    });
    if (!failure) setAddInstanceFor(null);
    return failure;
  };

  const handleDeleteTopic = (topic: Topic) => {
    if (!topic.name) return;
    setDeleteTopicError(null);
    setConfirmDeleteTopic(topic);
  };

  const confirmDeleteTopicNow = async () => {
    if (!confirmDeleteTopic?.name) return;
    const failure = await muts.deleteTopic(confirmDeleteTopic.name);
    // Stay open on failure and show the reason IN the dialog — without this the spinner
    // just stops and the dialog body is unchanged, so a denied user reads it as "nothing
    // happened" and clicks Delete again.
    setDeleteTopicError(failure);
    if (!failure) setConfirmDeleteTopic(null);
  };

  const handleCycle = async (topic: Topic, inst: TopicInstance) => {
    if (!topic.name || !inst.subTopicName) return;
    const next = nextNotes(inst.notes);
    await muts.upsertInstance({
      topicName: topic.name,
      industry: inst.industry || '_',
      subTopicName: inst.subTopicName,
      notes: next,
    });
  };

  const handleDeleteInstance = (topic: Topic, inst: TopicInstance) => {
    if (!topic.name || !inst.subTopicName) return;
    setDeleteInstanceError(null);
    setConfirmDeleteInstance({ topic, inst });
  };

  const confirmDeleteInstanceNow = async () => {
    if (!confirmDeleteInstance) return;
    const { topic, inst } = confirmDeleteInstance;
    if (!topic.name || !inst.subTopicName) return;
    const failure = await muts.deleteInstance({
      topicName: topic.name,
      industry: inst.industry || '_',
      subTopicName: inst.subTopicName,
    });
    setDeleteInstanceError(failure);
    if (!failure) setConfirmDeleteInstance(null);
  };

  const handleDragStart = (_e: DragEvent<HTMLDivElement>, topic: Topic) => {
    dragSrcRef.current = topic.name ?? null;
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, target: Topic) => {
    e.preventDefault();
    const src = dragSrcRef.current;
    dragSrcRef.current = null;
    if (!src || !target.name || src === target.name) return;
    const current = (domOrder ?? topics.map((t) => t.name ?? '').filter(Boolean)).slice();
    const fromIdx = current.indexOf(src);
    const toIdx = current.indexOf(target.name);
    if (fromIdx === -1 || toIdx === -1) return;
    current.splice(fromIdx, 1);
    current.splice(toIdx, 0, src);
    setDomOrder(current);
  };

  return (
    <div className="ivt-topic-view">

      {muts.mutationError && (
        <Alert
          type="error"
          dismissible
          onDismiss={() => muts.dismissMutationError()}
        >
          {muts.mutationError}
        </Alert>
      )}

      {orderedTopics.length === 0 ? (
        <div className="ivt-solution-map-page__empty">
          {editMode ? '暂无 Topic。点击 "+ 新建 Topic" 开始。' : '暂无 Topic。进入编辑模式添加。'}
        </div>
      ) : (
        <div className="ivt-topic-grid">
          {orderedTopics.map((t) => (
            <TopicCard
              key={t.name ?? ''}
              topic={t}
              editMode={editMode}
              locked={muts.isLocked(t.name ?? '')}
              onAddInstance={(topic) => topic.name && setAddInstanceFor(topic)}
              onDeleteTopic={handleDeleteTopic}
              onCycleInstancePriority={handleCycle}
              onDeleteInstance={handleDeleteInstance}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </div>
      )}

      {openTopic && (
        <TopicMetadataModal
          topic={openTopic}
          editable={editMode}
          saving={muts.isLocked(openTopic.name ?? '')}
          onClose={() => setOpenTopic(null)}
          onSave={async (input) => {
            const failure = await muts.upsertTopic(input);
            if (!failure) setOpenTopic(null);
            return failure;
          }}
        />
      )}

      {showNewTopic && (
        <TopicMetadataModal
          // No `topic` prop → modal renders in create mode with editable Name field.
          // Lock surface: useTopicMutations keys per-topic locks by topicName, which only
          // exists AFTER the user types one, so the hook's lock cannot gate the first
          // Save — this host tracks its own in-flight flag instead (see creatingTopic).
          // Failure also surfaces via mutationError on the page.
          editable
          saving={creatingTopic}
          onClose={() => setShowNewTopic(false)}
          onSave={handleSaveNewTopic}
        />
      )}

      {addInstanceFor && (
        <AddInstanceModal
          topicName={addInstanceFor.name ?? ''}
          saving={muts.isLocked(addInstanceFor.name ?? '')}
          onClose={() => setAddInstanceFor(null)}
          onSave={handleSaveInstance}
        />
      )}

      {confirmDeleteTopic && (
        <ConfirmDialog
          title="Delete Topic"
          message={
            <>
              确认删除 Topic <strong>"{confirmDeleteTopic.name}"</strong> 及其所有 UC 关联？
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          busy={muts.isLocked(confirmDeleteTopic.name ?? '')}
          error={deleteTopicError}
          onCancel={() => setConfirmDeleteTopic(null)}
          onConfirm={confirmDeleteTopicNow}
        />
      )}

      {confirmDeleteInstance && (
        <ConfirmDialog
          title="Delete Use Case"
          message={
            <>
              确认从 Topic <strong>"{confirmDeleteInstance.topic.name}"</strong> 移除 UC{' '}
              <strong>"{confirmDeleteInstance.inst.subTopicName}"</strong>
              {confirmDeleteInstance.inst.industry &&
                confirmDeleteInstance.inst.industry !== '_' && (
                  <>
                    {' '}
                    （行业：{confirmDeleteInstance.inst.industry}）
                  </>
                )}
              ？
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          busy={muts.isLocked(confirmDeleteInstance.topic.name ?? '')}
          error={deleteInstanceError}
          onCancel={() => setConfirmDeleteInstance(null)}
          onConfirm={confirmDeleteInstanceNow}
        />
      )}
    </div>
  );
}
